/**
 * Como se instala la app en ESTE aparato.
 *
 * Instalarla no es lo mismo en todos lados, y prometer un boton donde no lo hay
 * es peor que no ofrecer nada:
 *
 *   - Chrome, Edge, Opera y Brave —en Android y en la computadora— avisan que se
 *     puede instalar (`beforeinstallprompt`) y dejan abrir su propio cuadro de
 *     instalacion desde un boton nuestro. Es la via buena: UN toque.
 *   - Safari en iPhone y iPad no deja: hay que ir a Compartir -> "Agregar a
 *     inicio". Los demas navegadores del iPhone —Chrome, Edge, Firefox— son
 *     Safari por dentro y lo hacen igual, desde iOS 16.4.
 *   - Samsung Internet y Firefox en Android lo tienen en su menu.
 *   - Safari en Mac (17 en adelante): Archivo -> Agregar al Dock.
 *   - Dentro de otra app (el navegador de Facebook, Instagram, TikTok...) no se
 *     puede: hay que abrirla en el navegador de verdad.
 *   - Firefox en la computadora no instala apps web.
 *
 * Aqui solo se DECIDE: entra el `DeviceEnv` y sale la via, para probar cada
 * aparato sin tenerlo (`install-path.test.ts`). Es la logica de Xokoyotl
 * (`lib/instalar/como.ts`), que ya se midio con los aparatos reales.
 */

import { isIos, type DeviceEnv } from './device.js';

export type Browser =
  | 'ios-safari'
  /** Chrome, Edge, Firefox... en iPhone o iPad: Safari por dentro. */
  | 'ios-other'
  | 'android-chromium'
  | 'android-samsung'
  | 'android-firefox'
  /** Safari 17 o mas nuevo en Mac. */
  | 'mac-safari'
  | 'desktop-chromium'
  /** El navegador de otra app: Facebook, Instagram, TikTok... */
  | 'in-app-ios'
  | 'in-app-android'
  /** Firefox de escritorio, Safari viejo en Mac, lo que no se reconoce. */
  | 'unsupported';

/*
 * Las apps que abren los enlaces en su propia ventana y se dejan reconocer.
 * `GSA` es la app de Google en iPhone. WhatsApp NO aparece: en Android abre los
 * enlaces en una pestaña de Chrome y en iPhone en una de Safari, con el
 * `userAgent` de cada uno; para esos casos esta la nota de los pasos.
 */
const IN_APP =
  /FBAN|FBAV|FB_IAB|Instagram|Line\/|MicroMessenger|Snapchat|TikTok|musical_ly|BytedanceWebview|LinkedInApp|Twitter|GSA\//;

export function recognize(env: DeviceEnv): Browser {
  const { ua } = env;

  if (isIos(env)) {
    // Una vista web de otra app no trae el sello `Safari/`.
    if (IN_APP.test(ua) || !/Safari\//.test(ua)) return 'in-app-ios';
    if (/CriOS|FxiOS|EdgiOS|OPiOS|OPT\//.test(ua)) return 'ios-other';
    return 'ios-safari';
  }

  if (/Android/.test(ua)) {
    // `; wv)` es la vista web de Android: la usa la ventana de otras apps.
    if (IN_APP.test(ua) || /; wv\)/.test(ua)) return 'in-app-android';
    if (/SamsungBrowser/.test(ua)) return 'android-samsung';
    if (/Firefox\//.test(ua)) return 'android-firefox';
    if (/Chrome\//.test(ua)) return 'android-chromium';
    return 'unsupported';
  }

  if (/Firefox\//.test(ua)) return 'unsupported';
  if (/Chrome\/|Chromium\/|Edg\//.test(ua)) return 'desktop-chromium';

  if (/Macintosh|Mac OS X/.test(ua) && /Safari\//.test(ua)) {
    const version = Number(/Version\/(\d+)/.exec(ua)?.[1] ?? 0);
    return version >= 17 ? 'mac-safari' : 'unsupported';
  }

  return 'unsupported';
}

/** Telefono o tableta: decide lo que se dice al terminar ("abrela desde su icono"). */
export const isMobileBrowser = (b: Browser): boolean =>
  b.startsWith('ios-') || b.startsWith('android-') || b.startsWith('in-app-');

// ---------------------------------------------------------------------------
// Los pasos
// ---------------------------------------------------------------------------

export type StepIcon = 'share' | 'add' | 'menu' | 'menu-vertical' | 'menu-horizontal' | 'file' | 'install' | 'done';

/** Una accion, con la palabra que se busca en pantalla en negritas (`strong`). */
export interface Step {
  icon: StepIcon;
  before: string;
  strong: string;
  after?: string;
}

export interface Steps {
  steps: Step[];
  /** Por si lo que se ve no es lo que dicen los pasos. */
  note?: string;
}

const ADD: Step = { icon: 'done', before: 'Toca ', strong: 'Agregar' };

/**
 * Los pasos de cada navegador. `appName` es el nombre corto del negocio: es lo
 * que Chrome de escritorio pone en su menu ("Instalar San Carlos").
 */
export function installSteps(browser: Browser, appName: string): Steps | null {
  switch (browser) {
    case 'ios-safari':
      return {
        steps: [
          { icon: 'share', before: 'Toca ', strong: 'Compartir', after: '; si no lo ves, esta en el menu ···' },
          { icon: 'add', before: 'Elige ', strong: 'Agregar a inicio', after: ' (desliza la lista si no aparece)' },
          ADD,
        ],
        note: '¿No aparece «Agregar a inicio»? Estas en la ventana de otra app: abrela en Safari.',
      };
    case 'ios-other':
      return {
        steps: [
          { icon: 'share', before: 'Toca ', strong: 'Compartir', after: ', junto a la direccion o en el menu' },
          { icon: 'add', before: 'Elige ', strong: 'Agregar a inicio', after: ' (desliza la lista si no aparece)' },
          ADD,
        ],
        note: 'Si este navegador no lo ofrece, abrela en Safari.',
      };
    case 'android-chromium':
      return {
        steps: [
          { icon: 'menu-vertical', before: 'Abre el menu ', strong: '⋮', after: ' de arriba a la derecha' },
          { icon: 'install', before: 'Elige ', strong: 'Instalar app', after: ' o «Agregar a la pantalla principal»' },
          { icon: 'done', before: 'Toca ', strong: 'Instalar' },
        ],
        note: '¿No aparece? Abrela en Chrome: menu ⋮ → «Abrir en Chrome».',
      };
    case 'android-samsung':
      return {
        steps: [
          { icon: 'menu', before: 'Abre el menu ', strong: '≡', after: ' de abajo a la derecha' },
          { icon: 'add', before: 'Elige ', strong: 'Agregar pagina a', after: ' → «Pantalla de inicio»' },
          ADD,
        ],
      };
    case 'android-firefox':
      return {
        steps: [
          { icon: 'menu-vertical', before: 'Abre el menu ', strong: '⋮' },
          { icon: 'add', before: 'Elige ', strong: 'Agregar a la pantalla de inicio' },
          ADD,
        ],
      };
    case 'mac-safari':
      return {
        steps: [
          { icon: 'file', before: 'En la barra de menu, abre ', strong: 'Archivo' },
          { icon: 'add', before: 'Elige ', strong: 'Agregar al Dock' },
          ADD,
        ],
      };
    case 'desktop-chromium':
      return {
        steps: [
          { icon: 'install', before: 'Busca el icono ', strong: 'Instalar', after: ' al final de la barra de direcciones' },
          { icon: 'menu-vertical', before: 'O abre el menu del navegador y elige ', strong: `Instalar ${appName}` },
          { icon: 'done', before: 'Confirma con ', strong: 'Instalar' },
        ],
        note: 'Si ya la instalaste, abrela desde tus aplicaciones.',
      };
    case 'in-app-ios':
    case 'in-app-android':
    case 'unsupported':
      return null;
  }
}

// ---------------------------------------------------------------------------
// La via
// ---------------------------------------------------------------------------

export type InstallPath =
  /** Corre instalada, o el navegador dice que ya lo esta en este aparato. */
  | { kind: 'installed' }
  /** Un toque: el cuadro de instalacion del propio navegador. */
  | { kind: 'button' }
  | ({ kind: 'steps' } & Steps)
  /** Desde aqui no se puede: hay que abrirla en el navegador. */
  | { kind: 'open-in'; browser: 'Safari' | 'Chrome' }
  | { kind: 'impossible' };

export interface InstallSignals {
  /** Ya corre como app: `display-mode: standalone` y compañia. */
  standalone: boolean;
  /** El navegador ofrecio instalarla (`beforeinstallprompt`). */
  offer: boolean;
  /** El navegador dice que ya esta instalada en el aparato (`getInstalledRelatedApps`). */
  alreadyOnDevice: boolean;
}

export function installPath(browser: Browser, signals: InstallSignals, appName: string): InstallPath {
  if (signals.standalone || signals.alreadyOnDevice) return { kind: 'installed' };
  // La oferta del navegador gana a todo lo demas: es la via de un toque.
  if (signals.offer) return { kind: 'button' };
  if (browser === 'in-app-ios') return { kind: 'open-in', browser: 'Safari' };
  if (browser === 'in-app-android') return { kind: 'open-in', browser: 'Chrome' };
  const steps = installSteps(browser, appName);
  return steps ? { kind: 'steps', ...steps } : { kind: 'impossible' };
}

/**
 * Si se ofrece SOLA, sin que nadie la pida.
 *
 * Si cuando hay algo que hacer y se puede hacer aqui. No en la computadora con
 * Chrome o Edge sin oferta: ahi el navegador ofrece en cuanto se puede, y si no
 * ofrece es casi siempre porque ya esta instalada —volver a pedirlo seria
 * insistir—. Tampoco donde no se puede: un cartel que no lleva a nada solo
 * estorba. Las dos quedan en Configuracion, para quien la busque.
 */
export function offersItself(browser: Browser, path: InstallPath): boolean {
  switch (path.kind) {
    case 'button':
    case 'open-in':
      return true;
    case 'steps':
      return browser !== 'desktop-chromium';
    case 'installed':
    case 'impossible':
      return false;
  }
}

/**
 * Cuanto se espera antes de ofrecerla, para que la pantalla termine de
 * asentarse. En Android con Chrome, mas: la oferta del navegador puede tardar
 * unos segundos, y con ella basta un toque en vez de tres pasos.
 */
export const delayBeforeOffer = (browser: Browser, path: InstallPath): number =>
  path.kind === 'steps' && browser === 'android-chromium' ? 4_000 : 1_200;

/**
 * El enlace que abre esta direccion en Chrome desde la ventana de otra app.
 * Android lo entiende como "abrir con Chrome"; si Chrome no esta, sigue en la
 * misma direccion con el navegador que haya.
 */
export function openInChromeUrl(url: string): string {
  const u = new URL(url);
  const withoutScheme = `${u.host}${u.pathname}${u.search}`;
  return `intent://${withoutScheme}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(u.href)};end`;
}
